import React, { useCallback, useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { api, SuperadminExecutiveMetrics, ExecutiveDashboardData, CloudflareAnalytics } from '../api/client';

const PAGE_SIZE = 25;

type Tab = 'dashboard' | 'raw';
type Period = '7d' | '30d' | '90d' | 'all';

const PERIOD_LABELS: Record<Period, string> = {
  '7d': '7 Days',
  '30d': '30 Days',
  '90d': '90 Days',
  'all': 'All Time',
};

function formatNumber(value: number) {
  return value.toLocaleString('en-US');
}

function formatCents(cents: number) {
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function formatDashboardDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatDashboardDateLong(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function formatDateTime(value: string | null) {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatName(firstName: string | null, lastName: string | null) {
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  return fullName || '—';
}

function joinOrDash(values: string[], maxItems = 3) {
  if (!values.length) return '—';
  const visible = values.slice(0, maxItems).join(', ');
  return values.length > maxItems ? `${visible} +${values.length - maxItems}` : visible;
}

function truncate(value: string | null | undefined, max = 80) {
  if (!value) return '—';
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

function KpiCard({ title, value, subtitle }: { title: string; value: string; subtitle?: string }) {
  return (
    <div style={kpiCardStyle}>
      <div style={{ fontSize: '13px', color: '#6b7280', fontWeight: 500, marginBottom: '4px' }}>{title}</div>
      <div style={{ fontSize: '28px', fontWeight: 700, color: '#111827', lineHeight: 1.1 }}>{value}</div>
      {subtitle && <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{subtitle}</div>}
    </div>
  );
}

const kpiCardStyle: React.CSSProperties = {
  flex: '1 1 0',
  minWidth: '160px',
  background: '#fff',
  borderRadius: '12px',
  padding: '20px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  border: '1px solid #e5e7eb',
};

const VIEWER_PAGE_SIZE = 5;

function TrafficByPlatform({ viewers }: { viewers: ExecutiveDashboardData['viewerIntel'] }) {
  let appCount = 0;
  let webCount = 0;
  let unknownCount = 0;

  for (const v of viewers) {
    const ua = (v.userAgent || '').toLowerCase();
    if (ua.includes('picklecourts/') || (ua.includes('expo') && !ua.includes('exponent'))) {
      appCount++;
    } else if (ua.includes('mozilla/') || ua.includes('chrome/') || ua.includes('safari/') || ua.includes('firefox/')) {
      webCount++;
    } else {
      unknownCount++;
    }
  }

  const total = appCount + webCount + unknownCount;
  const pct = (n: number) => total > 0 ? `${Math.round((n / total) * 100)}%` : '0%';

  return (
    <section className="executive-panel" style={{ marginBottom: '24px' }}>
      <div className="executive-panel__header">
        <div className="executive-panel__title">Traffic by Platform</div>
      </div>
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', padding: '16px' }}>
        <div style={platformCardStyle}>
          <div style={{ fontSize: '24px', fontWeight: 700 }}>{formatNumber(appCount)}</div>
          <div style={{ fontSize: '13px', color: '#6b7280' }}>Mobile App</div>
          <div style={{ fontSize: '12px', color: '#9ca3af' }}>{pct(appCount)}</div>
        </div>
        <div style={platformCardStyle}>
          <div style={{ fontSize: '24px', fontWeight: 700 }}>{formatNumber(webCount)}</div>
          <div style={{ fontSize: '13px', color: '#6b7280' }}>Web Browser</div>
          <div style={{ fontSize: '12px', color: '#9ca3af' }}>{pct(webCount)}</div>
        </div>
        {unknownCount > 0 && (
          <div style={platformCardStyle}>
            <div style={{ fontSize: '24px', fontWeight: 700 }}>{formatNumber(unknownCount)}</div>
            <div style={{ fontSize: '13px', color: '#6b7280' }}>Other / Unknown</div>
            <div style={{ fontSize: '12px', color: '#9ca3af' }}>{pct(unknownCount)}</div>
          </div>
        )}
      </div>
      {total > 0 && (
        <div style={{ padding: '0 16px 16px', display: 'flex', height: '8px', borderRadius: '4px', overflow: 'hidden', background: '#f3f4f6' }}>
          {appCount > 0 && <div style={{ width: pct(appCount), background: '#6366f1', transition: 'width 0.3s' }} />}
          {webCount > 0 && <div style={{ width: pct(webCount), background: '#06b6d4', transition: 'width 0.3s' }} />}
          {unknownCount > 0 && <div style={{ width: pct(unknownCount), background: '#d1d5db', transition: 'width 0.3s' }} />}
        </div>
      )}
    </section>
  );
}

const platformCardStyle: React.CSSProperties = {
  flex: '1 1 0',
  minWidth: '120px',
  textAlign: 'center',
};

function LandingPageMetrics({ cfData, cfLoading }: { cfData: CloudflareAnalytics | null; cfLoading: boolean }) {
  if (cfLoading) {
    return (
      <section className="executive-panel" style={{ marginBottom: '24px' }}>
        <div className="executive-panel__header">
          <div className="executive-panel__title">Site Traffic</div>
        </div>
        <div className="loading" style={{ minHeight: '100px' }}><div className="spinner" /></div>
      </section>
    );
  }

  if (!cfData || !cfData.configured) {
    return (
      <section className="executive-panel" style={{ marginBottom: '24px' }}>
        <div className="executive-panel__header">
          <div className="executive-panel__title">Site Traffic</div>
        </div>
        <div style={{ padding: '16px', fontSize: '14px', color: '#6b7280' }}>
          Cloudflare analytics not configured. Set CLOUDFLARE_API_TOKEN and CLOUDFLARE_ZONE_ID on the backend.
        </div>
      </section>
    );
  }

  return (
    <section className="executive-panel" style={{ marginBottom: '24px' }}>
      <div className="executive-panel__header">
        <div>
          <div className="executive-panel__title">Site Traffic</div>
          <div className="executive-panel__subtitle">Cloudflare edge data · last {cfData.days} days · page views only (API calls, bots, and static assets excluded)</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
        <KpiCard title="Page Views" value={formatNumber(cfData.totalPageViews || 0)} subtitle={`${formatNumber(cfData.totalRequests || 0)} total requests`} />
        <KpiCard title="Landing Page" value={formatNumber(cfData.landingPageHits || 0)} subtitle="picklecourts.rent/" />
        <KpiCard title="Region Pages" value={formatNumber(cfData.regionPageHits || 0)} subtitle="/oklahoma/..." />
        <KpiCard title="App Views" value={formatNumber(cfData.appHits || 0)} subtitle="/app/" />
        <KpiCard title="US Traffic" value={formatNumber(cfData.usHits || 0)} />
      </div>

      {cfData.daily && cfData.daily.length > 0 && (
        <div style={{ width: '100%', height: 200, padding: '0 16px 16px' }}>
          <ResponsiveContainer>
            <BarChart data={cfData.daily}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => formatDashboardDate(String(v))} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip labelFormatter={(v) => formatDashboardDateLong(String(v))} />
              <Bar dataKey="pageViews" fill="#6366f1" radius={[4, 4, 0, 0]} name="Page Views" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', padding: '0 16px 16px' }}>
        {cfData.topPages && cfData.topPages.length > 0 && (
          <div>
            <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '8px', color: '#374151' }}>Top Pages</div>
            <table className="executive-table" style={{ fontSize: '13px' }}>
              <thead><tr><th>Path</th><th>Views</th></tr></thead>
              <tbody>
                {cfData.topPages.slice(0, 10).map((p, i) => (
                  <tr key={i}><td style={{ fontFamily: 'monospace' }}>{p.path}</td><td>{formatNumber(p.count)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {cfData.topCountries && cfData.topCountries.length > 0 && (
          <div>
            <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '8px', color: '#374151' }}>Top Countries</div>
            <table className="executive-table" style={{ fontSize: '13px' }}>
              <thead><tr><th>Country</th><th>Requests</th></tr></thead>
              <tbody>
                {cfData.topCountries.slice(0, 10).map((c, i) => (
                  <tr key={i}><td>{c.country}</td><td>{formatNumber(c.count)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ padding: '0 16px 16px', display: 'flex', gap: '16px', fontSize: '12px', color: '#9ca3af' }}>
        <span>WAF blocks: {formatNumber(cfData.wafBlocks || 0)}</span>
        <span>Scanner probes: {formatNumber(cfData.scannerTotal || 0)}</span>
      </div>
    </section>
  );
}

function VisitorJourney({ cfData, data }: { cfData: CloudflareAnalytics | null; data: ExecutiveDashboardData }) {
  if (!cfData?.configured) return null;

  const totalRegionViews = data.regionViews.reduce((s, r) => s + r.views, 0);
  const totalVenueViews = data.venueViews.reduce((s, v) => s + v.views, 0);
  const totalTimeslotClicks = data.timeslotClicks.reduce((s, t) => s + t.clicks, 0);
  const totalExternalClicks = data.externalBookingClicks.reduce((s, e) => s + e.clicks, 0);
  const totalBookings = data.kpis.confirmedBookingsInPeriod;

  const steps: Array<{ label: string; value: number; source: string }> = [
    { label: 'Landing Page Visits', value: cfData.landingPageHits || 0, source: 'Cloudflare' },
    { label: 'Region Page Visits', value: cfData.regionPageHits || 0, source: 'Cloudflare' },
    { label: 'Region Views (tracked)', value: totalRegionViews, source: 'App analytics' },
    { label: 'Venue Views', value: totalVenueViews, source: 'App analytics' },
    { label: 'Timeslot Clicks', value: totalTimeslotClicks, source: 'App analytics' },
    { label: 'External Booking Clicks', value: totalExternalClicks, source: 'App analytics' },
    { label: 'Confirmed Bookings', value: totalBookings, source: 'Database' },
  ];

  return (
    <section className="executive-panel" style={{ marginBottom: '24px' }}>
      <div className="executive-panel__header">
        <div>
          <div className="executive-panel__title">Visitor Journey</div>
          <div className="executive-panel__subtitle">Full funnel from landing page to booking · Cloudflare edge data paired with app event tracking</div>
        </div>
      </div>
      <div className="executive-table-wrap">
        <table className="executive-table">
          <thead>
            <tr>
              <th>Step</th>
              <th>Count</th>
              <th>Conversion</th>
              <th>Drop-off</th>
              <th>Source</th>
            </tr>
          </thead>
          <tbody>
            {steps.map((step, i) => {
              const prev = i > 0 ? steps[i - 1].value : null;
              const convPct = prev && prev > 0 ? ((step.value / prev) * 100) : null;
              const dropped = prev != null ? prev - step.value : null;
              return (
                <tr key={i}>
                  <td style={{ fontWeight: 600 }}>{step.label}</td>
                  <td style={{ fontSize: '16px', fontWeight: 700 }}>{formatNumber(step.value)}</td>
                  <td>
                    {convPct !== null ? (
                      <span style={{ color: convPct < 20 ? '#ef4444' : convPct < 50 ? '#f59e0b' : '#10b981', fontWeight: 600 }}>
                        {convPct.toFixed(1)}%
                      </span>
                    ) : '—'}
                  </td>
                  <td style={{ color: '#6b7280' }}>
                    {dropped != null && dropped > 0 ? `-${formatNumber(dropped)}` : dropped === 0 ? '—' : '—'}
                  </td>
                  <td style={{ fontSize: '12px', color: '#9ca3af' }}>{step.source}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function DashboardTab({ data, period }: { data: ExecutiveDashboardData; period: string }) {
  const k = data.kpis;
  const trafficQuality = data.trafficQuality;
  const [viewerPage, setViewerPage] = useState(0);
  const [cfData, setCfData] = useState<CloudflareAnalytics | null>(null);
  const [cfLoading, setCfLoading] = useState(true);

  const cfDays = period === '7d' ? 7 : period === '30d' ? 30 : period === '90d' ? 30 : 7;
  useEffect(() => {
    let cancelled = false;
    setCfLoading(true);
    api.getCloudflareAnalytics(cfDays).then((d) => { if (!cancelled) setCfData(d); })
      .catch(() => { if (!cancelled) setCfData(null); })
      .finally(() => { if (!cancelled) setCfLoading(false); });
    return () => { cancelled = true; };
  }, [cfDays]);

  const venueMap = new Map<string, {
    venueName: string;
    views: number;
    uniqueVisitors: number;
    timeslotClicks: number;
    confirmedBookings: number;
    revenueCents: number;
    externalClicks: number;
  }>();

  for (const v of data.bookingsByVenue) {
    venueMap.set(v.venueId, {
      venueName: v.venueName,
      views: 0,
      uniqueVisitors: 0,
      timeslotClicks: 0,
      confirmedBookings: v.confirmedCount,
      revenueCents: v.revenueCents,
      externalClicks: 0,
    });
  }
  for (const v of data.venueViews) {
    const existing = venueMap.get(v.venueId);
    if (existing) {
      existing.views = v.views;
      existing.uniqueVisitors = v.uniqueVisitors;
    } else {
      venueMap.set(v.venueId, {
        venueName: v.venueName,
        views: v.views,
        uniqueVisitors: v.uniqueVisitors,
        timeslotClicks: 0,
        confirmedBookings: 0,
        revenueCents: 0,
        externalClicks: 0,
      });
    }
  }
  for (const v of data.timeslotClicks) {
    const existing = venueMap.get(v.venueId);
    if (existing) existing.timeslotClicks = v.clicks;
  }
  for (const v of data.externalBookingClicks) {
    const existing = venueMap.get(v.venueId);
    if (existing) existing.externalClicks = v.clicks;
  }

  const venueScorecards = Array.from(venueMap.values()).sort((a, b) => b.views - a.views);
  const totalExternalClicks = data.externalBookingClicks.reduce((s, v) => s + v.clicks, 0);

  return (
    <>
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '32px' }}>
        <KpiCard title="Total Users" value={formatNumber(k.totalUsers)} subtitle={`+${formatNumber(k.newUsersInPeriod)} this period`} />
        <KpiCard title="Total Bookings" value={formatNumber(k.totalBookings)} subtitle={`${formatNumber(k.confirmedBookingsInPeriod)} approved/confirmed this period`} />
        <KpiCard title="Revenue" value={formatCents(k.revenueInPeriodCents)} subtitle={`${formatCents(k.platformFeeInPeriodCents)} platform fees`} />
        <KpiCard title="Promos" value={`${formatNumber(k.promosRedeemed)} / ${formatNumber(k.promosConsumed)}`} subtitle="Redeemed / Consumed" />
        <KpiCard title="External Clicks" value={formatNumber(totalExternalClicks)} subtitle="Redirects to venue sites" />
      </div>

      <section className="executive-panel" style={{ marginBottom: '24px' }}>
        <div className="executive-panel__header">
          <div>
            <div className="executive-panel__title">Traffic Quality</div>
            <div className="executive-panel__subtitle">
              Internal and tester traffic is excluded from visitor metrics by email, domain, IP, fingerprint, and dev-tool user-agent rules.
            </div>
          </div>
        </div>
        <div style={{ fontSize: '14px', color: '#374151' }}>
          Excluded {formatNumber(trafficQuality.excludedEventCount)} events across {formatNumber(trafficQuality.excludedViewerCount)} visitors in this period.
        </div>
      </section>

      <LandingPageMetrics cfData={cfData} cfLoading={cfLoading} />
      <VisitorJourney cfData={cfData} data={data} />

      {data.regionViews.length > 0 && (
        <section className="executive-panel" style={{ marginBottom: '24px' }}>
          <div className="executive-panel__header">
            <div className="executive-panel__title">Region Breakdown</div>
          </div>
          <div className="executive-table-wrap">
            <table className="executive-table">
              <thead>
                <tr>
                  <th>Region</th>
                  <th>Page Views</th>
                  <th>Unique Visitors</th>
                </tr>
              </thead>
              <tbody>
                {data.regionViews.map((r, i) => (
                  <tr key={i}>
                    <td>{r.regionName}</td>
                    <td>{formatNumber(r.views)}</td>
                    <td>{formatNumber(r.uniqueVisitors)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {data.viewerIntel.length > 0 && (() => {
        const totalViewers = data.viewerIntel.length;
        const totalPages = Math.ceil(totalViewers / VIEWER_PAGE_SIZE);
        const pagedViewers = data.viewerIntel.slice(viewerPage * VIEWER_PAGE_SIZE, (viewerPage + 1) * VIEWER_PAGE_SIZE);

        return (
          <section className="executive-panel" style={{ marginBottom: '24px' }}>
            <div className="executive-panel__header">
              <div>
                <div className="executive-panel__title">Viewer Intel</div>
                <div className="executive-panel__subtitle">
                  Showing {viewerPage * VIEWER_PAGE_SIZE + 1}–{Math.min((viewerPage + 1) * VIEWER_PAGE_SIZE, totalViewers)} of {formatNumber(totalViewers)} visitors · grouped by fingerprint or IP fallback
                </div>
              </div>
              {totalPages > 1 && (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => setViewerPage((p) => Math.max(p - 1, 0))} disabled={viewerPage === 0}>
                    Previous
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setViewerPage((p) => Math.min(p + 1, totalPages - 1))} disabled={viewerPage >= totalPages - 1}>
                    Next
                  </button>
                </div>
              )}
            </div>
            <div className="executive-table-wrap">
              <table className="executive-table">
                <thead>
                  <tr>
                    <th>Viewer</th>
                    <th>IP</th>
                    <th>Seen</th>
                    <th>Activity</th>
                    <th>Regions</th>
                    <th>Venues</th>
                    <th>Agent</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedViewers.map((viewer) => (
                    <tr key={viewer.viewerKey}>
                      <td>
                        <div style={{ fontWeight: 600 }}>
                          {formatName(viewer.firstName, viewer.lastName) !== '—' ? formatName(viewer.firstName, viewer.lastName) : truncate(viewer.deviceFingerprint || viewer.viewerKey, 32)}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>{viewer.userEmail || 'Anonymous visitor'}</div>
                        <div style={{ fontSize: '12px', color: viewer.isBot ? '#b45309' : '#9ca3af' }}>
                          {viewer.isBot ? 'Likely bot' : 'Likely human'} · {truncate(viewer.deviceFingerprint, 28)}
                        </div>
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{truncate(viewer.ipAddress, 24)}</td>
                      <td>
                        <div>{formatDateTime(viewer.lastSeenAt)}</div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>First {formatDateTime(viewer.firstSeenAt)}</div>
                      </td>
                      <td>
                        <div>{formatNumber(viewer.eventCount)} total</div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          R {formatNumber(viewer.regionViewCount)} · V {formatNumber(viewer.venueViewCount)} · S {formatNumber(viewer.timeslotClickCount)} · E {formatNumber(viewer.externalBookingClickCount)}
                        </div>
                      </td>
                      <td>{joinOrDash(viewer.regions)}</td>
                      <td>{joinOrDash(viewer.venues)}</td>
                      <td title={viewer.userAgent || undefined}>{truncate(viewer.userAgent, 64)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        );
      })()}

      <TrafficByPlatform viewers={data.viewerIntel} />

      {venueScorecards.length > 0 && (
        <section className="executive-panel" style={{ marginBottom: '24px' }}>
          <div className="executive-panel__header">
            <div className="executive-panel__title">Venue Scorecard</div>
            <div className="executive-panel__subtitle">Full funnel: view → intent → book</div>
          </div>
          <div className="executive-table-wrap">
            <table className="executive-table">
              <thead>
                <tr>
                  <th>Venue</th>
                  <th>Page Views</th>
                  <th>Unique Visitors</th>
                  <th>Timeslot Clicks</th>
                  <th>Approved / Confirmed</th>
                  <th>Revenue</th>
                  <th>External Clicks</th>
                </tr>
              </thead>
              <tbody>
                {venueScorecards.map((v, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 600 }}>{v.venueName}</td>
                    <td>{formatNumber(v.views)}</td>
                    <td>{formatNumber(v.uniqueVisitors)}</td>
                    <td>{formatNumber(v.timeslotClicks)}</td>
                    <td>{formatNumber(v.confirmedBookings)}</td>
                    <td>{formatCents(v.revenueCents)}</td>
                    <td>{formatNumber(v.externalClicks)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {data.bookingsOverTime.length > 0 && (
        <section className="executive-panel" style={{ marginBottom: '24px' }}>
          <div className="executive-panel__header">
            <div className="executive-panel__title">Bookings Over Time</div>
          </div>
          <div style={{ width: '100%', height: 300, padding: '16px' }}>
            <ResponsiveContainer>
              <BarChart data={data.bookingsOverTime}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => formatDashboardDate(String(v))} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip labelFormatter={(v) => formatDashboardDateLong(String(v))} />
                <Bar dataKey="count" fill="#6366f1" radius={[4, 4, 0, 0]} name="Bookings" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}

      {data.promoSummary.length > 0 && (
        <section className="executive-panel" style={{ marginBottom: '24px' }}>
          <div className="executive-panel__header">
            <div className="executive-panel__title">Promo Summary</div>
          </div>
          <div className="executive-table-wrap">
            <table className="executive-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Venue</th>
                  <th>% Off</th>
                  <th>Redeemed</th>
                  <th>Consumed</th>
                </tr>
              </thead>
              <tbody>
                {data.promoSummary.map((p, i) => (
                  <tr key={i}>
                    <td style={{ fontFamily: 'monospace' }}>{p.code}</td>
                    <td>{p.venueName || 'All'}</td>
                    <td>{p.percentOff}%</td>
                    <td>{formatNumber(p.redeemed)}</td>
                    <td>{formatNumber(p.consumed)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        <section className="executive-panel">
          <div className="executive-panel__header">
            <div className="executive-panel__title">Recent Bookings</div>
          </div>
          <div className="executive-table-wrap">
            <table className="executive-table">
              <thead>
                <tr>
                  <th>Created</th>
                  <th>Status</th>
                  <th>Customer</th>
                  <th>Venue</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {data.recentBookings.map((b) => (
                  <tr key={b.id}>
                    <td>{formatDateTime(b.createdAt)}</td>
                    <td>{b.status}</td>
                    <td>{b.customerName}</td>
                    <td>{b.venueName}</td>
                    <td>{b.paymentAmountCents ? formatCents(b.paymentAmountCents) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="executive-panel">
          <div className="executive-panel__header">
            <div className="executive-panel__title">Recent Users</div>
          </div>
          <div className="executive-table-wrap">
            <table className="executive-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Joined</th>
                </tr>
              </thead>
              <tbody>
                {data.recentUsers.map((u) => (
                  <tr key={u.id}>
                    <td>{formatName(u.firstName, u.lastName)}</td>
                    <td>{u.email}</td>
                    <td>{formatDateTime(u.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </>
  );
}

function RawDataTab() {
  const [metrics, setMetrics] = useState<SuperadminExecutiveMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usersPage, setUsersPage] = useState(1);
  const [bookingsPage, setBookingsPage] = useState(1);

  const fetchMetrics = useCallback(async (nextUsersPage = usersPage, nextBookingsPage = bookingsPage) => {
    try {
      setError(null);
      const response = await api.getSuperadminExecutiveMetrics({
        usersPage: nextUsersPage,
        usersLimit: PAGE_SIZE,
        bookingsPage: nextBookingsPage,
        bookingsLimit: PAGE_SIZE,
      });
      setMetrics(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load executive metrics');
    } finally {
      setLoading(false);
    }
  }, [usersPage, bookingsPage]);

  useEffect(() => {
    fetchMetrics(usersPage, bookingsPage);
  }, [fetchMetrics, usersPage, bookingsPage]);

  const users = metrics?.users.rows || [];
  const bookings = metrics?.bookings.rows || [];

  if (loading && !metrics) {
    return <div className="loading" style={{ minHeight: '300px' }}><div className="spinner" /></div>;
  }

  return (
    <>
      {error && <div className="alert alert-error" style={{ marginBottom: '24px' }}>{error}</div>}

      {metrics && (
        <>
          <section className="executive-panel" style={{ marginBottom: '24px' }}>
            <div className="executive-panel__header">
              <div>
                <div className="executive-panel__title">Users</div>
                <div className="executive-panel__subtitle">
                  Page {metrics.users.page} of {metrics.users.totalPages} · {formatNumber(metrics.users.total)} total users
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button className="btn btn-secondary btn-sm" onClick={() => setUsersPage((page) => Math.max(page - 1, 1))} disabled={loading || metrics.users.page <= 1}>
                  Previous
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => setUsersPage((page) => Math.min(page + 1, metrics.users.totalPages))} disabled={loading || metrics.users.page >= metrics.users.totalPages}>
                  Next
                </button>
              </div>
            </div>

            <div className="executive-table-wrap">
              <table className="executive-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Created</th>
                    <th>Bookings</th>
                    <th>Last Booking</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td>{formatName(user.firstName, user.lastName)}</td>
                      <td>{user.email}</td>
                      <td>{user.phone || '—'}</td>
                      <td>{formatDateTime(user.createdAt)}</td>
                      <td>{formatNumber(user.bookingCount)}</td>
                      <td>{formatDateTime(user.lastBookingCreatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="executive-panel">
            <div className="executive-panel__header">
              <div>
                <div className="executive-panel__title">Bookings</div>
                <div className="executive-panel__subtitle">
                  Page {metrics.bookings.page} of {metrics.bookings.totalPages} · {formatNumber(metrics.bookings.total)} total bookings
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button className="btn btn-secondary btn-sm" onClick={() => setBookingsPage((page) => Math.max(page - 1, 1))} disabled={loading || metrics.bookings.page <= 1}>
                  Previous
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => setBookingsPage((page) => Math.min(page + 1, metrics.bookings.totalPages))} disabled={loading || metrics.bookings.page >= metrics.bookings.totalPages}>
                  Next
                </button>
              </div>
            </div>

            <div className="executive-table-wrap">
              <table className="executive-table">
                <thead>
                  <tr>
                    <th>Created</th>
                    <th>Status</th>
                    <th>Customer</th>
                    <th>Email</th>
                    <th>Venue</th>
                    <th>Court</th>
                    <th>Start</th>
                    <th>Confirmation</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((booking) => (
                    <tr key={booking.id}>
                      <td>{formatDateTime(booking.createdAt)}</td>
                      <td>{booking.status}</td>
                      <td>{booking.customerName}</td>
                      <td>{booking.customerEmail || '—'}</td>
                      <td>{booking.venueName}</td>
                      <td>{booking.courtName}</td>
                      <td>{formatDateTime(booking.startTime)}</td>
                      <td>{booking.confirmationCode}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </>
  );
}

export default function ExecutiveScreen() {
  const [tab, setTab] = useState<Tab>('dashboard');
  const [period, setPeriod] = useState<Period>('30d');
  const [dashboardData, setDashboardData] = useState<ExecutiveDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getExecutiveDashboard(period);
      setDashboardData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    if (tab === 'dashboard') {
      fetchDashboard();
    }
  }, [tab, fetchDashboard]);

  return (
    <>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', flexWrap: 'wrap', marginBottom: '24px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', color: 'var(--gray-900)' }}>Executive View</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--gray-500)', fontSize: '14px' }}>Platform analytics and venue value metrics</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <div style={{ display: 'flex', background: '#f3f4f6', borderRadius: '8px', padding: '2px' }}>
            <button
              onClick={() => setTab('dashboard')}
              style={{
                padding: '6px 16px',
                borderRadius: '6px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 600,
                background: tab === 'dashboard' ? '#fff' : 'transparent',
                color: tab === 'dashboard' ? '#111827' : '#6b7280',
                boxShadow: tab === 'dashboard' ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
              }}
            >
              Dashboard
            </button>
            <button
              onClick={() => setTab('raw')}
              style={{
                padding: '6px 16px',
                borderRadius: '6px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 600,
                background: tab === 'raw' ? '#fff' : 'transparent',
                color: tab === 'raw' ? '#111827' : '#6b7280',
                boxShadow: tab === 'raw' ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
              }}
            >
              Raw Data
            </button>
          </div>

          {tab === 'dashboard' && (
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value as Period)}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                fontSize: '13px',
                background: '#fff',
                cursor: 'pointer',
              }}
            >
              {(Object.keys(PERIOD_LABELS) as Period[]).map((p) => (
                <option key={p} value={p}>{PERIOD_LABELS[p]}</option>
              ))}
            </select>
          )}

          {tab === 'dashboard' && (
            <button className="btn btn-primary btn-sm" onClick={fetchDashboard} disabled={loading}>
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
          )}
        </div>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: '24px' }}>{error}</div>}

      {tab === 'dashboard' && (
        loading && !dashboardData ? (
          <div className="loading" style={{ minHeight: '300px' }}><div className="spinner" /></div>
        ) : dashboardData ? (
          <DashboardTab data={dashboardData} period={period} />
        ) : null
      )}

      {tab === 'raw' && <RawDataTab />}
    </>
  );
}
